import React from "react";
import { Link } from "react-router-dom";
import InnerHero from "../../components/InnerHero";
import { Beaker, Calendar, CheckCircle2 } from "lucide-react";

const campusLifeLinks = [
  ["Hostels", "/campus-life/hostels"],
  ["Mess & Canteen", "/campus-life/mess-and-canteen"],
  ["Transportation", "/campus-life/transportation"],
  ["Sports & Gym", "/campus-life/sports-gym/overview"],
  ["ATM", "/campus-life/atm"],
  ["Wi-Fi Campus", "/campus-life/wifi-campus"],
  ["Solar Rooftop", "/campus-life/solar-rooftop"],
  ["Computer Center", "/campus-life/computer-center"],
  ["Co-operative Society", "/campus-life/cooperative-society"],
  ["Welding Research Center", "/campus-life/welding-research-center"],
  ["IIT Bombay Spoken Tutorial", "/campus-life/iit-bombay-spoken-tutorial"],
];

export const CampusLifeShell = ({
  title,
  short,
  image,
  navItems,
  quickFacts,
  children,
}) => (
  <main>
    <InnerHero
      title={title}
      image={image}
      breadcrumbs={[
        { label: "Life" },
        { label: "Campus Life" },
        { label: short || title },
      ]}
    />

    <section className="bg-surface-alt/60 border-b border-brand/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap gap-x-6 gap-y-2 text-xs font-sans-ui font-semibold tracking-wider text-brand">
        {navItems.map(([href, label]) => (
          <a key={href} href={href} className="uppercase hover:underline">
            {label}
          </a>
        ))}
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-12">
      <div className="grid gap-8 lg:grid-cols-3 lg:gap-10">
        <div className="min-w-0 lg:col-span-2 space-y-8 sm:space-y-12">{children}</div>
        <CampusLifeAside currentTitle={title} quickFacts={quickFacts} />
      </div>
    </section>
  </main>
);

export const CampusLifeAside = ({ currentTitle, quickFacts }) => (
  <aside className="space-y-5">
    <div className="bg-white border border-brand/15 p-4 sm:p-5 lg:sticky lg:top-36 self-start">
      <QuickFactsCard facts={quickFacts} />

      <div className="mt-6 pt-5 border-t border-brand/10">
        <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
          Other Campus Life
        </h4>
        <ul className="space-y-1.5">
          {campusLifeLinks
            .filter(([label]) => label !== currentTitle)
            .map(([label, to]) => (
              <li key={to}>
                <Link
                  to={to}
                  className="text-sm text-[#2a2a2a] hover:text-brand flex items-center gap-1"
                >
                  <Beaker size={12} className="text-brand" />
                  {label}
                </Link>
              </li>
            ))}
        </ul>
      </div>
    </div>
  </aside>
);

export const QuickFactsCard = ({ facts }) => (
  <>
    <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui">
      Quick Facts
    </h4>
    <div className="mt-3 space-y-3 text-sm">
      {facts.map(([label, value]) => (
        <div key={label} className="flex items-start gap-2">
          <Calendar size={14} className="text-brand flex-shrink-0 mt-0.5" />
          <span>
            {label}: <strong>{value}</strong>
          </span>
        </div>
      ))}
    </div>
  </>
);

export const PageSection = ({ id, title, icon: Icon, children }) => (
  <section id={id} className="scroll-mt-32">
    <div className="flex items-end justify-between gap-4 mb-4 sm:mb-5">
      <h3
        className="text-xl sm:text-2xl md:text-3xl text-brand font-semibold flex items-center gap-2"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {Icon && <Icon size={20} className="shrink-0" />} {title}
      </h3>
    </div>
    {children}
  </section>
);

export const InfoList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((text) => (
      <li key={text} className="flex items-start gap-3 bg-white p-3 sm:p-4 border border-brand/15">
        <CheckCircle2 size={18} className="text-brand flex-shrink-0 mt-0.5" />
        <span className="text-sm sm:text-[15px] text-[#2a2a2a]">{text}</span>
      </li>
    ))}
  </ul>
);

export const ResponsiveTable = ({ columns, rows }) => (
  <div className="overflow-x-auto bg-white border border-brand/15">
    <table className="min-w-[560px] sm:min-w-full text-sm text-left">
      <thead className="bg-brand text-surface">
        <tr>
          {columns.map((column) => (
            <th key={column} className="px-4 py-3 font-semibold whitespace-nowrap">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={`${row.join("-")}-${rowIndex}`} className="border-t border-brand/10">
            {row.map((cell, cellIndex) => (
              <td key={`${cell}-${cellIndex}`} className="px-4 py-3 align-top text-[#2a2a2a]">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
